import { CircleIcon } from '@phosphor-icons/react';
import * as RadioGroupPrimitive from '@radix-ui/react-radio-group';
import * as React from 'react';

import { SECTION_LABEL } from '@/lib/ui-tokens';
import { cn } from '@/lib/utils';

const RadioGroup = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Root>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Root>
>(({ className, ...props }, ref) => {
  return <RadioGroupPrimitive.Root className={cn('grid gap-2', className)} {...props} ref={ref} />;
});
RadioGroup.displayName = 'RadioGroup';

const RadioGroupItem = React.forwardRef<
  React.ElementRef<typeof RadioGroupPrimitive.Item>,
  React.ComponentPropsWithoutRef<typeof RadioGroupPrimitive.Item>
>(({ className, ...props }, ref) => {
  return (
    <RadioGroupPrimitive.Item
      ref={ref}
      className={cn(
        'aspect-square h-4 w-4 rounded-full border border-border text-primary focus:outline-none focus-visible:ring-2 focus-visible:ring-ring disabled:cursor-not-allowed disabled:opacity-50',
        className,
      )}
      {...props}
    >
      <RadioGroupPrimitive.Indicator className="flex items-center justify-center">
        <CircleIcon className="h-2.5 w-2.5 fill-current text-current" />
      </RadioGroupPrimitive.Indicator>
    </RadioGroupPrimitive.Item>
  );
});
RadioGroupItem.displayName = 'RadioGroupItem';

export { RadioGroup, RadioGroupItem };

export interface FilterRadioOption<V extends string> {
  value: V;
  label: string;
  /**
   * Optional leading glyph. Added for the campaigns channel filter, where the
   * platform mark is genuinely faster to scan than the word and is the design
   * system's licensed exception to monochrome. Optional so every existing
   * text-only filter list is unchanged.
   */
  icon?: React.ReactNode;
  detail?: string;
}

interface FilterRadioListProps<V extends string> {
  label: string;
  value: V;
  options: ReadonlyArray<FilterRadioOption<V>>;
  onValueChange: (v: V) => void;
}

export function FilterRadioList<V extends string>({
  label,
  value,
  options,
  onValueChange,
}: FilterRadioListProps<V>) {
  return (
    <div className="space-y-2">
      <div className={cn('px-2 py-1.5', SECTION_LABEL)}>{label}</div>
      <div role="radiogroup" aria-label={label} className="flex flex-col gap-1">
        {options.map((option) => (
          <button
            key={option.value}
            type="button"
            role="radio"
            aria-checked={value === option.value}
            onClick={() => {
              if (option.value !== value) onValueChange(option.value);
            }}
            className={cn(
              'flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-caption transition-colors sm:min-h-0',
              value === option.value
                ? 'bg-muted text-foreground'
                : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
            )}
          >
            {option.icon ? (
              <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
                {option.icon}
              </span>
            ) : null}
            <span className="min-w-0 truncate">{option.label}</span>
          </button>
        ))}
      </div>
    </div>
  );
}

interface FilterCheckboxListProps<V extends string> {
  label: string;
  /** Every currently-selected value. Empty array = no narrowing. */
  values: ReadonlyArray<V>;
  options: ReadonlyArray<FilterRadioOption<V>>;
  onToggle: (v: V) => void;
  /**
   * Lay the rows out in two columns. For a long list of short labels
   * (countries), one column per row wastes the popover's height.
   */
  columns?: 1 | 2;
}

/**
 * The MULTI-select sibling of `FilterRadioList`, with deliberately identical
 * grammar: same `SECTION_LABEL` header, same row height, same `text-caption`,
 * same selected/unselected colour pair.
 *
 * WHY THIS EXISTS. `/discover/ads` needed multi-select country and platform
 * facets and hand-rolled `Checkbox` + `<label>` rows inside its own popover,
 * beside a `FilterRadioList` for format. The two grammars sat two rows apart and
 * did not match — the founder's report was literally _"you see here the
 * difference in text and how those look"_. A shared primitive is the only way
 * multi-select facets stop drifting from single-select ones every time a new
 * surface needs both.
 *
 * Selection is shown by the same filled row as the radio list rather than a
 * checkbox glyph, so a popover mixing both reads as one control family. The
 * `aria-checked` + `role="checkbox"` pair keeps the multi-select semantics
 * honest for assistive tech even though the visual is a row.
 */
export function FilterCheckboxList<V extends string>({
  label,
  values,
  options,
  onToggle,
  columns = 1,
}: FilterCheckboxListProps<V>) {
  return (
    <div className="space-y-2">
      <div className={cn('px-2 py-1.5', SECTION_LABEL)}>{label}</div>
      <div
        role="group"
        aria-label={label}
        className={cn('gap-1', columns === 2 ? 'grid grid-cols-2' : 'flex flex-col')}
      >
        {options.map((option) => {
          const checked = values.includes(option.value);
          return (
            <button
              key={option.value}
              type="button"
              role="checkbox"
              aria-checked={checked}
              onClick={() => onToggle(option.value)}
              className={cn(
                'flex min-h-11 w-full items-center gap-2 rounded-md px-2 py-1.5 text-left text-caption transition-colors sm:min-h-0',
                checked
                  ? 'bg-muted text-foreground'
                  : 'text-muted-foreground hover:bg-muted/50 hover:text-foreground',
              )}
            >
              {option.icon ? (
                <span className="flex size-4 shrink-0 items-center justify-center" aria-hidden>
                  {option.icon}
                </span>
              ) : null}
              <span
                className="min-w-0 flex-1 truncate"
                title={option.detail ? `${option.label} · ${option.detail}` : option.label}
              >
                {option.label}
              </span>
              {option.detail ? (
                <span className="shrink-0 text-detail text-muted-foreground">{option.detail}</span>
              ) : null}
            </button>
          );
        })}
      </div>
    </div>
  );
}
